import { EventBus } from "@/event-bus";
import ZorgIDHubService from "@/services/ZorgIDHubService";
import CreateSessionResultModel from "@/models/security/zorgID/createSessionResultModel";
import SessionOpenedViewModel from "@/models/security/zorgID/sessionOpenedViewModel";
import SecureElementViewModel from "@/models/security/zorgID/secureElementViewModel";
import SessionClosedViewModel from "@/models/security/zorgID/sessionClosedViewModel";
import CertificateWarningsViewModel from "@/models/security/zorgID/certificateWarningsViewModel";
import { AZNCONNECT_API } from "@/services/aznconnect-api";

const moment = require("moment");

export class SecurityService {
  //#region Singleton

  private static instance: SecurityService;

  private constructor() {
    // do something construct...
    this.start();
  }

  static getInstance() {
    if (!SecurityService.instance) {
      SecurityService.instance = new SecurityService();
      // ... any one time initialization goes here ...
    }

    return SecurityService.instance;
  }

  //#endregion

  //#region SignalR

  private zorgIDHubService: ZorgIDHubService = new ZorgIDHubService();

  private start() {
    var me = this;

    me.loadValues();

    if (me.clientInstanceGuid === null || me.clientInstanceGuid === "") {
      AZNCONNECT_API.get<string>("/zorgid/GetClientInstanceGuid")
        .then((response: any) => {
          me.clientInstanceGuid = response.data;
          me.subscribeToEvents();
          me.zorgIDHubService.startSignalR();
        })
        .catch((error: any) => {
          console.log(error);
        });
    } else {
      me.subscribeToEvents();
      me.zorgIDHubService.startSignalR();
    }
  }

  private stop() {
    this.zorgIDHubService.stopSignalR();
  }

  private subscribeToEvents(): void {
    var me = this;

    me.zorgIDHubService.subscribeConnectionEstablished((x: any) => {
      console.log("SecurityService: zorgid hub connected", me.isLoggedOn());

      if (me.isLoggedOn()) {
        me.zorgIDHubService.bindClientInstanceGuidToZorgIDSessionId(
          me.clientInstanceGuid,
          me.sessionId,
        );
      }
    });

    me.zorgIDHubService.subscribeBindedClientInstanceGuidToZorgIDSessionIdReceived(
      (x: any) => {
        console.log("sendClientInstanceGuid result: " + x);

        if (me.isLoggedOn()) {
          me.zorgIDHubService.knownSession(me.sessionId);
        }
      },
    );

    me.zorgIDHubService.subscribeKnownSessionReceived(x => {
      console.log("SecurityService: KnownSession received");

      if (x) {
        console.log("Session is known");

        me.zorgIDHubService.retrieveSecureElement(
          me.clientInstanceGuid,
          me.sessionId,
        );
        me.updateLoggedOnUserInUI();
      } else {
        console.log("Session is unkown logging out");

        me.loggedOff();
      }
    });

    me.zorgIDHubService.subscribeCertificateWarningsReceived(
      (x: CertificateWarningsViewModel) => {
        console.log("SecurityService: Certificate Warnings Received");
        console.log(x.Message);

        EventBus.$emit("application_message_component_error", x.DutchMessage);
      },
    );

    me.zorgIDHubService.subscribeSessionInitializedReceived(
      (x: CreateSessionResultModel) => {
        console.log("SecurityService: SessionInitialized Received");

        me.sessionId = x.SessionId as string;
      },
    );

    me.zorgIDHubService.subscribeSessionOpenedReceived(
      (x: SessionOpenedViewModel) => {
        console.log("SecurityService: SessionOpened Received");

        if (x.IsCancelled) {
          EventBus.$emit("application_message_component_error", x.Status);

          me.loggedOff();
        } else {
          me.loggedOn(x);
        }
      },
    );

    me.zorgIDHubService.subscribeSecureElementRetrievedReceived(
      (x: SecureElementViewModel) => {
        console.log("SecurityService: SecureElementViewModel Received");

        if (x.IsCancelled) {
          EventBus.$emit(
            "application_message_component_error",
            x.CancelledReasonDisplayValue,
          );

          me.loggedOff();
        }
      },
    );

    me.zorgIDHubService.subscribeSessionClosedReceived(
      (x: SessionClosedViewModel) => {
        console.log("SecurityService: SessionClosedViewModel Received");

        if (x.Reason == 2 || x.Reason == 3) {
          // SignerRequested || ApplicationRequested
          EventBus.$emit("application_message_component_info", x.DisplayValue);
        } else {
          EventBus.$emit("application_message_component_error", x.DisplayValue);
        }

        SecurityService.getInstance().loggedOff();
      },
    );
  }

  public subscribeSessionInitializedReceived(
    method: (model: CreateSessionResultModel) => void,
  ) {
    this.zorgIDHubService.subscribeSessionInitializedReceived(method);
  }

  public initializeSession() {
    this.zorgIDHubService.initializeSession(this.clientInstanceGuid);
  }

  public subscribeSessionOpenedReceived(
    method: (model: SessionOpenedViewModel) => void,
  ) {
    this.zorgIDHubService.subscribeSessionOpenedReceived(method);
  }

  public subscribeSecureElementRetrievedReceived(
    method: (model: SecureElementViewModel) => void,
  ) {
    this.zorgIDHubService.subscribeSecureElementRetrievedReceived(method);
  }

  public retrieveSecureElement(sessionId: string) {
    this.zorgIDHubService.retrieveSecureElement(
      this.clientInstanceGuid,
      sessionId,
    );
  }

  public subscribeSessionClosedReceived(
    method: (model: SessionClosedViewModel) => void,
  ) {
    this.zorgIDHubService.subscribeSessionClosedReceived(method);
  }

  public closeSession(sessionId: string) {
    this.zorgIDHubService.closeSession(this.clientInstanceGuid, sessionId);
  }

  //#endregion

  public readonly zorgIDAuthorizationScheme: string = "ZORGID";

  public readonly CLIENT_INSTANCE_GUID: string = "CLIENT_INSTANCE_GUID";

  public clientInstanceGuid!: string;

  private readonly SESSION_ID_KEY: string = "ZORGID_SESSION_ID";

  public sessionId!: string;

  private readonly PERSON_NAME_KEY: string = "PERSON_NAME_KEY";

  public personName!: string;

  private readonly UZI_PASS_LABEL_KEY: string = "UZI_PASS_LABEL_KEY";

  public uziPassLabel!: string;

  private readonly UZI_NUMBER_KEY: string = "UZI_NUMBER_KEY";

  public uziNumber!: string;

  private readonly UZI_REGISTER_SUBSCRIBER_NUMBER_KEY: string =
    "UZI_REGISTER_SUBSCRIBER_NUMBER_KEY";

  public uziRegisterSubscriberNumber!: string;

  public loggedOn(sessionOpenedViewModel: SessionOpenedViewModel) {
    this.setValues(
      this.clientInstanceGuid,
      sessionOpenedViewModel.SessionId,
      sessionOpenedViewModel.SecureElement.PassholderName || "",
      sessionOpenedViewModel.SecureElement.Label || "",
      sessionOpenedViewModel.SecureElement.UziNumber || "",
      sessionOpenedViewModel.SecureElement.UziRegisterSubscriberNumber || "",
    );
    var now = new Date();
    var nowString = moment(now).format("DD MMMM YYYY HH:mm");
    const msg = `${nowString}: U bent ingelogd als ${this.personName} (${
      this.uziPassLabel
    })`;

    EventBus.$emit("application_message_component_success", msg);
    EventBus.$emit("SecurityService_loggedon_user");
  }

  public isLoggedOn(): boolean {
    if (
      this.sessionId !== undefined &&
      this.sessionId !== null &&
      this.sessionId !== ""
    ) {
      return true;
    }

    return false;
  }

  public loggedOff() {
    this.reset();
    //Force reload to prevent information from showing on the page without permission
    //This is needed for the acceptance of the UZI certification. We currently reload the page using the simpel JS location reload,
    //because the $router is unavailable in this particular class.
    window.location.reload();
  }

  private reset() {
    this.setValues(this.clientInstanceGuid, "", "", "", "", "");
  }

  private setValues(
    clientInstanceGuid: string,
    sessionId: string,
    personName: string,
    uziPassLabel: string,
    uziNumber: string,
    uziRegisterSubscriberNumber: string,
  ) {
    var me = this;

    me.clientInstanceGuid = clientInstanceGuid;
    me.saveValue(me.CLIENT_INSTANCE_GUID, clientInstanceGuid);

    me.sessionId = sessionId;
    me.saveValue(me.SESSION_ID_KEY, sessionId);
    me.personName = personName;
    me.saveValue(me.PERSON_NAME_KEY, personName);
    me.uziPassLabel = uziPassLabel;
    me.saveValue(me.UZI_PASS_LABEL_KEY, uziPassLabel);
    me.uziNumber = uziNumber;
    me.saveValue(me.UZI_NUMBER_KEY, uziNumber);
    me.uziRegisterSubscriberNumber = uziRegisterSubscriberNumber;
    me.saveValue(
      me.UZI_REGISTER_SUBSCRIBER_NUMBER_KEY,
      uziRegisterSubscriberNumber,
    );
    this.updateLoggedOnUserInUI();
  }

  private updateLoggedOnUserInUI() {
    EventBus.$emit("SecurityService_loggedon_user_updated");
  }

  private loadValues() {
    var me = this;
    var clientInstanceGuid = this.loadValue(me.CLIENT_INSTANCE_GUID);
    var sessionId = this.loadValue(me.SESSION_ID_KEY);
    var personName = this.loadValue(me.PERSON_NAME_KEY);
    var uziPassLabel = this.loadValue(me.UZI_PASS_LABEL_KEY);
    var uziNumber = this.loadValue(me.UZI_NUMBER_KEY);
    var uziRegisterSubscriberNumber = this.loadValue(
      me.UZI_REGISTER_SUBSCRIBER_NUMBER_KEY,
    );
    me.setValues(
      clientInstanceGuid,
      sessionId,
      personName,
      uziPassLabel,
      uziNumber,
      uziRegisterSubscriberNumber,
    );
  }

  private loadValue(key: string): string {
    return sessionStorage.getItem(key) || "";
  }

  private saveValue(key: string, val: string) {
    sessionStorage.setItem(key, val);
  }
}
